import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PopupWrapper from '../../../../components/popup/PopupWrapper';
import { getButtonClass } from '../../../../components/popup/popupUtils';
import { formatAllianceMemberRoleLabel } from '../alli/members/allianceMemberRoleLabels';

/**
 * 멤버 퇴장 처리 3단계 공통 팝업
 * config.initialData: { member, getTransferCandidates, getNewOwnerCandidates, allMembersWithOrg }
 * onClose(submit, { newOwnerId, transferToId, fileHandling, reason })
 */
const MemberRemovePopup = ({ config, onClose }) => {
  const { t } = useTranslation();
  const {
    member,
    spaceOwnerUid,
    purgeArchiveMode,
    getTransferCandidates,
    getNewOwnerCandidates,
    allMembersWithOrg,
    summary,
  } = config?.initialData || {};
  const [step, setStep] = useState(1);
  const [newOwnerId, setNewOwnerId] = useState('');
  const [transferToId, setTransferToId] = useState('');
  const [fileHandling, setFileHandling] = useState('transfer');
  const [reason, setReason] = useState('');

  const transferCandidates = useMemo(() => (member && getTransferCandidates ? getTransferCandidates(member) : []), [member, getTransferCandidates]);
  const newOwnerCandidates = useMemo(() => (member && getNewOwnerCandidates ? getNewOwnerCandidates(member) : []), [member, getNewOwnerCandidates]);
  const isOwner =
    member?.role === 'Consortium Owner'
    || (spaceOwnerUid != null
      && member?.userUid != null
      && String(member.userUid) === String(spaceOwnerUid));
  const needNewOwner = !purgeArchiveMode && isOwner && newOwnerCandidates.length > 0;
  const canProceedStep1 = (needNewOwner ? newOwnerId && newOwnerCandidates.length > 0 : true);
  const transferToMember = allMembersWithOrg?.find((m) => m.id === transferToId);
  const newOwnerMember = allMembersWithOrg?.find((m) => m.id === newOwnerId);
  const hasTransferTarget = Boolean(transferToId);

  useEffect(() => {
    if (!hasTransferTarget && fileHandling === 'transfer') {
      setFileHandling('common');
    }
  }, [hasTransferTarget, fileHandling]);

  const handleCancel = () => onClose(false);
  const handleStep1Next = () => setStep(2);
  const handleStep2Next = () => setStep(3);
  const handleStep2Prev = () => setStep(1);
  const handleStep3Prev = () => setStep(2);
  const handleConfirm = () => onClose(true, { newOwnerId, transferToId, fileHandling, reason });

  if (!member) return null;

  const flowTitle = purgeArchiveMode
    ? t('popup.memberRemove.purgeTitle')
    : t('popup.memberRemove.removeTitle');

  return (
    <PopupWrapper
      config={{
        ...config,
        title: `${flowTitle} ${step}/3 — ${member.name} (${formatAllianceMemberRoleLabel(member.role)})`,
        width: config?.width ?? 520,
        leftButton: { text: t('popup.common.cancel'), color: 'btn-secondary', submit: false },
        rightButton: {
          text: purgeArchiveMode ? t('popup.memberRemove.purgeExecute') : t('popup.memberRemove.complete'),
          color: 'btn-danger',
          submit: true
        },
      }}
      onClose={onClose}
      displayType={config?.displayType || 'modal'}
      className="popup-member-remove"
    >
      <div className="main-popup-body">
        <p className="small text-muted mb-3">
          {purgeArchiveMode
            ? t('popup.memberRemove.purgeGuide')
            : t('popup.memberRemove.removeGuide')}
        </p>

        {step === 1 && (
          <>
            {needNewOwner && (
              <div className="mb-4">
                <p className="small fw-medium mb-2">{t('popup.memberRemove.step1OwnerTransfer')}</p>
                <label className="form-label small">{t('popup.memberRemove.newOwnerLabel')}</label>
                <select
                  className="form-select form-select-sm"
                  value={newOwnerId}
                  onChange={(e) => setNewOwnerId(e.target.value)}
                >
                  <option value="">{t('popup.common.select')}</option>
                  {newOwnerCandidates.map((m) => (
                    <option key={m.id} value={m.id}>{m.name} ({m.orgName}) — {formatAllianceMemberRoleLabel(m.role)}</option>
                  ))}
                </select>
                <p className="small text-muted mt-1 mb-0">{t('popup.memberRemove.newOwnerHelp')}</p>
                {newOwnerCandidates.length === 0 && <p className="small text-danger mt-1 mb-0">{t('popup.memberRemove.newOwnerMissingError')}</p>}
              </div>
            )}
            <p className="small fw-medium mb-2">{t('popup.memberRemove.step1WorkSummary')}</p>
            <div className="border rounded p-3 bg-light mb-3">
              <p className="small fw-medium mb-2">{t('popup.memberRemove.currentItemsTitle')}</p>
              <ul className="small mb-0 ps-3">
                <li>{t('popup.memberRemove.activePostCount', { count: Number(summary?.activePostCount ?? 0) })}</li>
                <li>{t('popup.memberRemove.documentPostCount', { count: Number(summary?.documentPostCount ?? 0) })}</li>
                <li>{t('popup.memberRemove.milestoneCounts', { projectCount: Number(summary?.milestoneProjectCount ?? 0), itemCount: Number(summary?.milestoneItemCount ?? 0) })}</li>
              </ul>
            </div>
            <label className="form-label small">{t('popup.memberRemove.transferTargetLabel')}</label>
            <select
              className="form-select form-select-sm"
              value={transferToId}
              onChange={(e) => setTransferToId(e.target.value)}
            >
              <option value="">{t('popup.common.select')}</option>
              {transferCandidates.map((m) => (
                <option key={m.id} value={m.id}>{m.name} ({formatAllianceMemberRoleLabel(m.role)})</option>
              ))}
            </select>
            {transferCandidates.length === 0 && <p className="small text-muted mt-1">{t('popup.memberRemove.noTransferHint')}</p>}
          </>
        )}

        {step === 2 && (
          <>
            <p className="small fw-medium mb-2">{t('popup.memberRemove.step2Title')}</p>
            <div className="form-check mb-2">
              <input type="radio" name="fileHandling" id="fh-transfer" className="form-check-input" checked={fileHandling === 'transfer'} onChange={() => setFileHandling('transfer')} disabled={!hasTransferTarget} />
              <label className="form-check-label" htmlFor="fh-transfer">{transferToMember ? t('popup.memberRemove.transferToMember', { name: transferToMember.name }) : t('popup.memberRemove.transferToTarget')}</label>
            </div>
            <div className="form-check mb-2">
              <input type="radio" name="fileHandling" id="fh-common" className="form-check-input" checked={fileHandling === 'common'} onChange={() => setFileHandling('common')} />
              <label className="form-check-label" htmlFor="fh-common">{t('popup.memberRemove.transferToCommon')}</label>
            </div>
            <div className="form-check mb-3">
              <input type="radio" name="fileHandling" id="fh-direct" className="form-check-input" checked={fileHandling === 'direct'} onChange={() => setFileHandling('direct')} />
              <label className="form-check-label" htmlFor="fh-direct">{t('popup.memberRemove.directHandling', { name: member.name })}</label>
            </div>
            <label className="form-label small">{t('popup.memberRemove.reasonLabel')}</label>
            <input type="text" className="form-control form-control-sm" placeholder={t('popup.memberRemove.reasonPlaceholder')} value={reason} onChange={(e) => setReason(e.target.value)} />
          </>
        )}

        {step === 3 && (
          <>
            <p className="small fw-medium mb-2">{t('popup.memberRemove.step3Title')}</p>
            <ul className="small list-unstyled mb-2">
              <li>{t('popup.memberRemove.summaryTarget', { name: member.name, org: member.orgName })}</li>
              {needNewOwner && newOwnerMember && <li>{t('popup.memberRemove.summaryNewOwner', { name: newOwnerMember.name })}</li>}
              <li>{t('popup.memberRemove.summaryTransferWork', { name: transferToMember ? transferToMember.name : '-' })}</li>
              <li>{t('popup.memberRemove.summaryFileHandling', { mode: fileHandling === 'transfer' && transferToMember ? t('popup.memberRemove.transferToMemberShort', { name: transferToMember.name }) : fileHandling === 'common' ? t('popup.memberRemove.transferToCommon') : t('popup.memberRemove.directHandlingShort') })}</li>
              <li>{purgeArchiveMode ? t('popup.memberRemove.summaryPurgeRow') : t('popup.memberRemove.summaryArchive')}</li>
            </ul>
            {!purgeArchiveMode ? (
              <p className="small text-muted mb-3">{t('popup.memberRemove.notifyAfterComplete', { name: member.name })}</p>
            ) : null}
            <div className="alert alert-warning small mb-0">{t('popup.memberRemove.irreversibleWarning')}</div>
          </>
        )}
      </div>
      <div className="main-popup-footer d-flex justify-content-between">
        {step === 1 && (
          <>
            <button type="button" className={`btn ${getButtonClass('btn-outline-secondary')}`} onClick={handleCancel}>{t('popup.common.cancel')}</button>
            <button type="button" className={`btn ${getButtonClass('btn-primary')}`} onClick={handleStep1Next} disabled={!canProceedStep1}>{t('popup.memberRemove.nextStep')}</button>
          </>
        )}
        {step === 2 && (
          <>
            <button type="button" className={`btn ${getButtonClass('btn-outline-secondary')}`} onClick={handleStep2Prev}>{t('popup.memberRemove.previousStep')}</button>
            <button type="button" className={`btn ${getButtonClass('btn-outline-secondary')}`} onClick={handleCancel}>{t('popup.common.cancel')}</button>
            <button type="button" className={`btn ${getButtonClass('btn-primary')}`} onClick={handleStep2Next}>{t('popup.memberRemove.nextStep')}</button>
          </>
        )}
        {step === 3 && (
          <>
            <button type="button" className={`btn ${getButtonClass('btn-outline-secondary')}`} onClick={handleStep3Prev}>{t('popup.memberRemove.previousStep')}</button>
            <button type="button" className={`btn ${getButtonClass('btn-danger')}`} onClick={handleConfirm}>
              {purgeArchiveMode ? t('popup.memberRemove.purgeExecute') : t('popup.memberRemove.complete')}
            </button>
          </>
        )}
      </div>
    </PopupWrapper>
  );
};

export default MemberRemovePopup;
